import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import useNavegacionTeclado from '../../hooks/useNavegacionTeclado';

const Wrapper = styled.div`
  position: relative;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: ${({ $size }) => ($size === 'sm' ? '0.3rem 2rem 0.3rem 0.5rem' : '0.4rem 2rem 0.4rem 0.6rem')} !important;
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.borderDefault)};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme, $isEmpty }) => ($isEmpty ? theme.colors.txtSecondary : theme.colors.txtPrincipal)};
  font-size: ${({ $size }) => ($size === 'sm' ? '0.8rem' : '0.875rem')};
  transition-property: border-color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease;
  min-height: ${({ $size }) => ($size === 'sm' ? '30px' : '34px')};
  text-align: left;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  &:focus {
    outline: none;
    border-color: ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.primary)};
    box-shadow: 0 0 0 3px ${({ theme, $invalid }) => ($invalid ? `${theme.colors.danger}20` : `${theme.colors.primary}20`)};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surfaceDisabled};
    color: ${({ theme }) => theme.colors.txtSecondary};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.borderHover || theme.colors.borderDefault)};
  }
`;

const Dropdown = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.dropdownOverModal || 9500};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  max-height: 30vh;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: opacity 160ms ease, transform 160ms ease;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: ${(p) => (p.$open ? 'translateY(0)' : p.$dropUp ? 'translateY(6px)' : 'translateY(-6px)')};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
`;

// $selected = valor guardado. $activo = fila resaltada con las flechas; gana
// sobre el valor guardado y sobre el hover mientras se navega con el teclado.
const Option = styled.div`
  padding: 8px 10px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ $selected, $activo, theme }) => (
    $activo ? theme.colors.opcionActivaFondo
      : $selected ? theme.colors.primaryAlpha
        : theme.colors.surface
  )};
  box-shadow: ${({ $selected, $activo, theme }) => (
    $activo ? `inset 3px 0 0 ${theme.colors.opcionActivaBorde}`
      : $selected ? `inset 3px 0 0 ${theme.colors.primary}`
        : 'none'
  )};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-weight: ${({ $selected, $activo, theme }) => (
    $selected || $activo ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.regular
  )};

  &:hover {
    background: ${({ $selected, $activo, theme }) => (
      $activo ? theme.colors.opcionActivaFondo
        : $selected ? theme.colors.primaryAlpha
          : theme.colors.surfaceHover
    )};
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.txtSecondary};
  pointer-events: none;
  flex-shrink: 0;
`;

const defaultGetOptionValue = (option) => String(option?.id ?? option?.value ?? '');
const defaultGetOptionLabel = (option) => String(option?.label ?? option?.nombre ?? option?.name ?? option?.descripcion ?? '');

export default function SimpleSelectField({
  className,
  options = [],
  value,
  onChange,
  getOptionValue = defaultGetOptionValue,
  getOptionLabel = defaultGetOptionLabel,
  placeholder = 'Seleccioná',
  disabled = false,
  size = 'sm',
  invalid = false,
  dropUp = false,
  saltarAlSiguiente = true,
}) {
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  // Buffer del tipeo directo (ver más abajo).
  const bufferRef = useRef({ texto: '', vencimiento: 0 });

  const selectedOption = options.find((option) => String(getOptionValue(option)) === String(value ?? ''));
  const displayLabel = selectedOption ? getOptionLabel(selectedOption) : placeholder;

  const elegirOpcion = (option) => {
    onChange?.(getOptionValue(option));
    setOpen(false);
  };

  const {
    indiceActivo,
    setIndiceActivo,
    onKeyDown: onKeyDownNavegacion,
    registrarOpcion,
    propsAriaInput,
  } = useNavegacionTeclado({
    items: options,
    abierto: open,
    setAbierto: setOpen,
    onElegir: elegirOpcion,
    onCerrar: () => setOpen(false),
    saltarAlSiguiente,
  });

  // Tipeo directo, como en un <select> nativo: escribir "die" salta a "Diesel".
  // Este campo se dibuja con un <button> (no un <select> real) para poder
  // controlar su aspecto, y esa decisión se había llevado puesta la búsqueda por
  // letras que el nativo trae de fábrica. Sin esto, un desplegable de 30 estados
  // obliga a bajar de a una fila con la flecha.
  const onKeyDown = (evento) => {
    const esLetra = evento.key.length === 1 && !evento.altKey && !evento.ctrlKey && !evento.metaKey;
    if (esLetra) {
      evento.preventDefault();
      const ahora = Date.now();
      // Las teclas encadenan mientras se escriba seguido; tras una pausa de 1s
      // se arranca una búsqueda nueva.
      const buffer = ahora > bufferRef.current.vencimiento ? '' : bufferRef.current.texto;
      const texto = buffer + evento.key.toLowerCase();
      bufferRef.current = { texto, vencimiento: ahora + 1000 };

      const indice = options.findIndex((o) => getOptionLabel(o).toLowerCase().startsWith(texto));
      if (indice >= 0) {
        if (!open) setOpen(true);
        setIndiceActivo(indice);
      }
      return;
    }
    // Espacio abre el desplegable, igual que un <select> nativo.
    if (evento.key === ' ' && !open) {
      evento.preventDefault();
      setOpen(true);
      return;
    }
    onKeyDownNavegacion(evento);
  };

  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const updatePosition = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const zoomRaw = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--app-zoom')
      );
      const zoom = zoomRaw > 0 ? zoomRaw : 1;
      const left = rect.left / zoom;
      const width = rect.width / zoom;
      if (dropUp) {
        setDropdownStyle({ left, width, bottom: window.innerHeight / zoom - rect.top / zoom + 4 });
      } else {
        setDropdownStyle({ left, width, top: rect.bottom / zoom + 4 });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, dropUp]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target) && !dropdownRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    // Escape lo maneja el onKeyDown del botón: un listener en document cerraba
    // todos los desplegables montados a la vez y se comía el Escape del modal.
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef} className={className}>
      <SelectButton
        {...propsAriaInput}
        aria-haspopup="listbox"
        $size={size}
        $invalid={invalid}
        $isEmpty={!selectedOption}
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={onKeyDown}
        type="button"
      >
        <span>{displayLabel}</span>
        <ChevronIcon>
          <i className="bi bi-chevron-down" />
        </ChevronIcon>
      </SelectButton>

      {open && !disabled && ReactDOM.createPortal(
        <Dropdown ref={dropdownRef} role="listbox" $open $dropUp={dropUp} style={dropdownStyle}>
          {options.map((option, indice) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const isSelected = String(optionValue) === String(value ?? '');
            return (
              <Option
                key={optionValue}
                id={`opcion-${indice}`}
                ref={registrarOpcion(indice)}
                role="option"
                aria-selected={isSelected}
                $selected={isSelected}
                $activo={indice === indiceActivo}
                onMouseEnter={() => setIndiceActivo(indice)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => elegirOpcion(option)}
              >
                {optionLabel}
              </Option>
            );
          })}
        </Dropdown>,
        document.body
      )}
    </Wrapper>
  );
}
